<script lang="ts" context="module">
  declare const window: any;
</script>

<script lang="ts">
  import moment from "moment";
  import { createEventDispatcher, onMount } from "svelte";
  import ProductList from "@retailer/svelte/Shared/QuickOrderList/ProductList.svelte";
  import NewListModal from "@retailer/svelte/Shared/QuickOrderList/NewListModal.svelte";
  import DeleteListModal from "@retailer/svelte/Shared/QuickOrderList/DeleteListModal.svelte";
  import ExpandArrow from "@retailer/svelte/Shared/QuickOrderList/ExpandArrow.svelte";
  import PopoverList from "@shared/svelte/Components/Popover/PopoverList.svelte";
  import Banner from "@shared/svelte/Components/Banner/Banner.svelte";
  import Flash from "@shared/services/flashv2";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import {
    QuickOrderListDetail,
    QuickOrderListFilters,
    ProtectedListName,
    ListType,
  } from "@shared/types/QuickOrderList";
  import type { QuickOrderListProduct } from "@shared/types/ProductLine";
  import { QuickOrderListBannersStore } from "@retailer/svelte/stores/QuickOrderListBannersStore";
  import type { PageData } from "@shared/types/PageData";
  import type { RetailerType } from "@shared/types/Retailer";
  import type { OpenProductGroup } from "@shared/types/OpenProductGroup";

  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { ProductListEntity } from "@shared/services/event_tracking/entities";
  import { getDataForInventories } from "@retailer/services/pricing_data_service";

  import { onDestroy } from "svelte";
  import { isEqual } from "lodash";
  import { smartFilter } from "@retailer/svelte/stores/QuickOrderListSmartFilterStore";
  import { EventTracking } from "@shared/services/event_tracking/events";

  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import ListTabs from "@retailer/svelte/Shared/QuickOrderList/ListTabs.svelte";

  export let retailer: RetailerType;
  export let list: QuickOrderListDetail;
  export let listType: ListType;
  export let numOfLists: number;
  export let filters: QuickOrderListFilters;
  export let expanded: boolean;
  export let page: PageData;
  export let products: QuickOrderListProduct[];
  export let callbacks;
  export let showLoading: boolean = false;
  export let productListEntity: ProductListEntity;

  let showInventory: boolean = false;

  const { trackClick } = EventTracking;

  const originalProductOrder = () => {
    return products.map(x => {
      return String(x.id);
    });
  };
  let dispatch = createEventDispatcher();

  let originalListOrder;
  let showModal: boolean = false;
  let showDeleteModal: boolean = false;
  let listName: string = "";
  let newListName = "";
  let originalListName = "";
  let editMode: boolean = false;
  let readOnly: boolean = true;
  let sortObj = null;
  page.sortOrder = [];
  let useListLayout: boolean = localStorage.getItem("listLayout")
    ? localStorage.getItem("listLayout") === "list"
    : !(page.params?.layout === "grid");
  let urlParams;

  let banners, showBanner;

  $: {
    if (stagedRolloutCheck("kevel_ads")) {
      banners = $QuickOrderListBannersStore?.banners?.[0]?.[0]?.banners;
    } else {
      banners = $QuickOrderListBannersStore.banners;
    }
  }
  $: {
    showBanner = banners?.length && !expanded;
  }
  $: bannerKey = showBanner ? banners[0].image_url : null;

  $: {
    list = list;
    page = page;
    editMode = page.params.editMode === "true" || page.params.editMode === true;
    if ($smartFilter.distributor) {
      listName = "Distributor / " + $smartFilter.distributor.name;
    } else {
      listName = list.name;
    }
    originalListName = listName;
    readOnly = list.auto_generated || retailer?.limited_ordering;
    urlParams = new URLSearchParams(window.location.search);
    originalListOrder = originalProductOrder();
  }

  $: limitedOrderingRetailer = retailer?.limited_ordering;
  $: inDynamicFiltersRollout = stagedRolloutCheck("adm__dynamic_limited_ordering_filters");
  $: displayListTabs =
    limitedOrderingRetailer && inDynamicFiltersRollout && listType === ListType.ApprovedProducts;
  let openProductGroups: OpenProductGroup[] = [];
  let productGroupsLoaded: boolean = false;
  let approvedProductsHeaderText = "Here you can browse items that are available to order.";

  const getOpenProductGroups = async () => {
    const res = await HttpPromise({
      method: "GET",
      url: "/api/retailer/limited_ordering_filters/open_product_groups",
    });
    if (res?.groups.length) {
      approvedProductsHeaderText =
        "Here you can browse lists or entire product groups that have items available to order.";
    }
    openProductGroups = res?.groups;
    productGroupsLoaded = true;
  };

  onMount(async () => {
    let inventoryIds = products
      .filter(qolp => !!qolp.product?.inventory_id)
      .map(qolp => String(qolp.product?.inventory_id));
    await getDataForInventories(inventoryIds); // Loads the cache for the CartWidget to use later
    if (displayListTabs) {
      getOpenProductGroups();
    } else {
      productGroupsLoaded = true;
    }
  });

  let expandSidebar = () => {
    dispatch("expandSidebar");
  };

  const downloadCSV = () => {
    let http: any = {};
    http = {
      method: "GET",
      url: "/api/retailer/quick_order_list_products/export_to_csv",
      data: {
        listId: list.id,
      },
    };

    return HttpPromise(http)
      .then(res => {
        return downloadCsvCallback(res.csv, listName);
      })
      .catch(res => {
        Flash.now({
          compact: true,
          type: "error",
          header: "Error",
          message: "There was error while processing this request.",
        });
      });
  };

  const downloadCsvCallback = (data: any, filename: string) => {
    let blob = new Blob([data], { type: "text/csv" });
    // @ts-ignore
    if (window.navigator.msSaveOrOpenBlob) {
      // @ts-ignore
      window.navigator.msSaveBlob(blob, filename);
    } else {
      let elem = window.document.createElement("a");
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `${filename}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }
  };

  const openModal = () => {
    showModal = true;
  };

  const openDeleteModal = () => {
    showDeleteModal = true;
  };

  const openEditMode = () => {
    Object.assign(page.params, {
      search: null,
      page: 0,
      container: null,
      distributor: null,
      product_type: null,
      category: null,
      sort: "position",
      direction: "ASC",
      deleted: null,
    });
    dispatch("selectFilter", { filters: { ...page.params, editMode: true } });
  };

  const cancelEdit = () => {
    list.name = originalListName;
    setPage(1);
    editMode = false;
  };

  const setPage = pageNumber => {
    Object.assign(page.params, { page: pageNumber, editMode: false, deleted: null });
    dispatch("selectFilter", { filters: page.params });
  };

  const updateList = async () => {
    if (
      list.name == ProtectedListName.MySavedProducts &&
      originalListName !== ProtectedListName.MySavedProducts
    ) {
      Flash.now({ type: "error", message: "Name has already been taken" });
      return;
    }

    HttpPromise({
      method: "PUT",
      url: `/api/retailer/quick_order_lists/${list.id}`,
      data: {
        name: list.name,
        quick_order_list_products: getUpdatedPositions(),
      },
    }).then(
      function (res) {
        editMode = false;
        setPage(1);
        Flash.now({ type: "success", message: "Success! List updated" });
      },
      function (res) {
        if (res.errors && res.errors.messages) {
          res.errors.messages.forEach((message: string) => {
            Flash.now({ type: "error", header: "Error", message });
          });
        }
      },
    );
  };

  const getUpdatedPositions = () => {
    if (!page.sortOrder) return [];

    return page.sortOrder.map((p, index) => ({
      position: index + 1,
      quick_order_list_product_id: parseInt(p.id),
    }));
  };

  const downloadAction = {
    name: "Download list",
    action: () => {
      downloadCSV();
    },
  };
  const editAction = { name: "Edit name & customize order", action: () => openEditMode() };
  const deleteAction = { name: "Delete list", action: () => openDeleteModal() };

  $: menuOptions = readOnly ? [downloadAction] : [editAction, downloadAction, deleteAction];

  const ShopProvi = window["C"].IMAGE_PATHS["shop-provi.png"];

  function unsavedChanges() {
    if (!editMode) {
      return false;
    }

    const nameChanged = list.name !== originalListName;
    const currentSort = page.sortOrder.map(product => product.id);
    const sortChanged = page.sortOrder.length !== 0 && !isEqual(originalListOrder, currentSort);

    return nameChanged || sortChanged;
  }

  window.beforeStateChange = confirmExit;

  onDestroy(() => (window.beforeStateChange = undefined));

  const confirmMsg = "Are you sure you want to leave this list with unsaved changes?";

  function confirmExit() {
    if (unsavedChanges()) {
      return confirm(confirmMsg);
    } else {
      return;
    }
  }

  function beforeUnload(event: any) {
    if (!unsavedChanges()) {
      return;
    } else {
      event.preventDefault();
      const returnValue = "";
      event.returnValue = returnValue; // Chrome requires event.returnValue to be set.
      return returnValue;
    }
  }

  let innerWidth;
  $: mobileModeBanner = innerWidth < 480;

  $: productListEntity = new ProductListEntity({
    quick_order_list_id: list.id,
    name: list.name,
    layout: useListLayout ? "list" : "grid",
    listType,
  });
</script>

<svelte:window on:beforeunload={beforeUnload} bind:innerWidth />
<TrackingContext entities={productListEntity}>
  {#if showBanner}
    {#key bannerKey}
      <div class="fizz-stack-8">
        <Banner {banners} mobileMode={mobileModeBanner} pageName={"lists"} />
      </div>
    {/key}
  {/if}
  {#if numOfLists > 0 && productGroupsLoaded}
    <div class="fizz-space-between-responsive">
      <div class="fizz-stack-16 fizz-flex-item">
        {#if editMode}
          {#if $LayoutStore.isMobile}
            <h3 class="fizz-heading-3 fizz-flex-item listName">
              <p class="fizz-text-regular fizz-text-large fizz-stack-16">Edit list</p>
              <input
                type="text"
                class="fizz-input-text fizz-full-width fizz-stack-16"
                data-testId="name-edit-input"
                disabled={listType === ListType.IntegratedDistributorOrderHistory}
                bind:value={list.name}
              />
              <div class="fizz-flex-row">
                <button
                  class="fizz-button-secondary"
                  disabled={!list.name.trim()}
                  on:click|preventDefault={updateList}>Save changes</button
                >
                <button class="fizz-button" on:click|preventDefault={cancelEdit}>Cancel</button>
              </div>
            </h3>
          {:else}
            <h3 class="fizz-heading-3 fizz-inline listName">
              <p class="fizz-text-regular fizz-text-large fizz-stack-16">Edit list</p>
              <div class="fizz-flex-row">
                <input
                  type="text"
                  class="fizz-input-text fizz-half-width"
                  data-testId="name-edit-input"
                  disabled={listType === ListType.IntegratedDistributorOrderHistory}
                  bind:value={list.name}
                />
                <button
                  class="fizz-button-secondary"
                  disabled={!list.name.trim()}
                  on:click|preventDefault={updateList}>Save changes</button
                >
                <button class="fizz-button" on:click|preventDefault={cancelEdit}>Cancel</button>
              </div>
            </h3>
          {/if}
        {:else}
          <div class="fizz-flow-8">
            <div class="fizz-space-between-responsive">
              <div class="fizz-space-between">
                <div class="fizz-flex-row">
                  <h3 class="fizz-heading-3 fizz-inline-8 listName" data-testid="listName">
                    {listName}{listType === ListType.Sculpture
                      ? ` (${moment(list.sculpture_report_date).format("MMMM D")})`
                      : ""}
                  </h3>
                  {#if !showInventory}
                    <div data-testid="dotdotdot-menu">
                      <PopoverList
                        expandedLight={true}
                        icon={{ type: "MoreVert", height: "20", width: "20" }}
                        item={null}
                        menuStyles="white-space: nowrap;"
                        openMenu={false}
                        options={menuOptions}
                        tooltip={!$LayoutStore.isMobile && "More list actions"}
                        useButtonElement={false}
                      />
                    </div>
                  {/if}
                </div>
                <h3 on:click|preventDefault={expandSidebar} on:keyup|preventDefault={expandSidebar}>
                  <ExpandArrow {expanded} />
                </h3>
              </div>
              {#if listType === ListType.Sculpture}
                <a href="/sculpture_reports">View full report</a>
              {/if}
            </div>
            {#if listType === ListType.RecentlyOrdered}
              <span class="fizz-text-subdued">Products from the past 5 orders</span>
            {/if}
            {#if listType === ListType.PreviouslyOrdered}
              <span class="fizz-text-subdued">
                Products you've previously ordered from your distributors.
              </span>
            {/if}
            {#if listType === ListType.MySavedProducts}
              <span>View all your products from your lists in one place</span>
            {/if}
            {#if listType === ListType.ApprovedProducts && productGroupsLoaded}
              <div class:fizz-half-width={!$LayoutStore.isMobile}>
                The following products have been approved by your corporate beverage team. {approvedProductsHeaderText}
              </div>
            {/if}
          </div>
        {/if}
      </div>
      {#if !editMode && !list.for_sculpture_retailer}
        <!-- svelte-ignore a11y-invalid-attribute -->

        <div class="fizz-space-between fizz-flex-row-32">
          {#if useListLayout}
            <div class="notifications-toggle fizz-stack-16 fizz-flex-row-8">
              <label class="fizz-toggle">
                <input
                  type="checkbox"
                  name="inventory-toggle"
                  id="inventory-toggle"
                  data-testId="inventory-toggle"
                  bind:checked={showInventory}
                />
                <span class="fizz-toggle-switch" />
                <span class="strong">Inventory</span>
              </label>
            </div>
          {:else if $LayoutStore.isMobile}
            <!-- so inventory toggle does not move when switching between list/grid view  -->
            <div />
          {/if}
          {#if !showInventory && !list.auto_generated}
            <a
              href={"#"}
              class="fizz-text-medium fizz-stack-16"
              on:click|preventDefault={() => {
                trackClick("list_add_products");
                callbacks.openListBuilder();
              }}
              data-testId="add-products-button"
            >
              <strong>Add Products</strong>
            </a>
          {/if}
        </div>
      {/if}
    </div>
    {#if displayListTabs && openProductGroups.length}
      <ListTabs
        {showInventory}
        {list}
        {page}
        {filters}
        {products}
        {openProductGroups}
        bind:editMode
        bind:sortObj
        on:selectFilter
        {readOnly}
        {callbacks}
        bind:useListLayout
        {showLoading}
        {listType}
      />
    {:else}
      <ProductList
        {showInventory}
        fullCount={list.full_count}
        {list}
        {page}
        {filters}
        {products}
        bind:editMode
        bind:sortObj
        on:selectFilter
        {readOnly}
        {callbacks}
        bind:useListLayout
        {showLoading}
        {listType}
      />
    {/if}
  {/if}

  {#if !limitedOrderingRetailer && numOfLists == 0}
    {#if $LayoutStore.isMobile}
      <div class="fizz-grid-center fizz-text-center">
        <img src={ShopProvi} alt="Shop Provi" width="93" />
        <div>
          <div class="default-subheader--bold padding-top--small padding-bottom--smallest">
            <p>Create your first list</p>
            <p>to see all saved products</p>
          </div>
          <p>Save time and stay organized when you add</p>
          <p>products you often order to a list! These can</p>
          <p>be your ordering guide or wish lists</p>
          <div class="padding-top--small fizz-grid-center">
            <button class="fizz-button-secondary" on:click|preventDefault={openModal}
              >Create a list</button
            >
          </div>
        </div>
      </div>
    {:else}
      <div class="fizz-flex-row-inline flex-center">
        <img src={ShopProvi} alt="Shop Provi" class="fizz-avatar" />
        <div>
          <p class="default-body--bold padding-bottom--smallest">
            Create your first list to see all saved products
          </p>
          <p>Save time and stay organized when you add products you often order to a list!</p>
          <p>These can be your ordering guide or wish lists</p>
          <div class="fizz-space-between padding-top--small">
            <button class="fizz-button-secondary" on:click|preventDefault={openModal}
              >Create a list</button
            >
          </div>
        </div>
      </div>
    {/if}
  {/if}
</TrackingContext>

<NewListModal bind:showModal {newListName} />
<DeleteListModal bind:showDeleteModal {list} />
